import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Coordinates } from '../models/WeatherData';

interface MapViewProps {
  onMapClick: (coords: Coordinates) => void;
  selectedCoordinates: Coordinates | null;
  initialCenter?: Coordinates; // Optional initial center position
  initialZoom?: number; // Optional initial zoom level
  userLocation?: Coordinates | null; // User's current location to show as blue dot
}

// Completely override default icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: '',
  iconRetinaUrl: '',
  shadowUrl: ''
});

// Create custom marker icon with proper SVG data URL
const svgIcon = `
<svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
  <ellipse cx="16" cy="37" rx="8" ry="3" fill="rgba(0,0,0,0.2)"/>
  <path d="M16 0C9.373 0 4 5.373 4 12c0 8.5 12 26 12 26s12-17.5 12-26c0-6.627-5.373-12-12-12z" 
        fill="#3B82F6" stroke="#1E40AF" stroke-width="1.5"/>
  <circle cx="16" cy="12" r="5" fill="white"/>
  <circle cx="16" cy="12" r="2.5" fill="#1E40AF"/>
</svg>
`;

const customIcon = L.icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(svgIcon),
  iconSize: [32, 40],
  iconAnchor: [16, 40],
  popupAnchor: [0, -40]
});

const MapView: React.FC<MapViewProps> = ({ 
  onMapClick, 
  selectedCoordinates,
  initialCenter,
  initialZoom = 19,
  userLocation
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const userMarkerRef = useRef<L.CircleMarker | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const initialCenterRef = useRef<Coordinates | null>(null); // Save initial center once
  const initialZoomRef = useRef(initialZoom); // Save initial zoom
  const mapInitializedRef = useRef(false); // Track if map has been initialized
  const onMapClickRef = useRef(onMapClick); // Store latest callback in ref

  // Update callback ref when it changes
  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);

  // Save initial center when it first becomes available
  useEffect(() => {
    if (initialCenter && !initialCenterRef.current) {
      initialCenterRef.current = initialCenter;
      console.log('📍 Saved initial center:', initialCenter);
    }
  }, [initialCenter]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    // Use saved initial center or Stockholm as default
    const center = initialCenterRef.current || { lat: 59.3293, lng: 18.0686 };
    const zoom = initialCenterRef.current ? initialZoomRef.current : 6; // Zoom in if we have initial position

    console.log('🗺️ Initializing map at:', center, 'zoom:', zoom);

    // Initialize map
    const map = L.map(mapContainerRef.current, {
      zoomControl: false,
    }).setView([center.lat, center.lng], zoom);

    mapInitializedRef.current = true;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    // Add zoom control to top right
    L.control.zoom({ position: 'topright' }).addTo(map);

    // Map click handler - use ref to avoid re-initialization when callback changes
    map.on('click', (e: L.LeafletMouseEvent) => {
      onMapClickRef.current({ lat: e.latlng.lat, lng: e.latlng.lng });
    });

    mapRef.current = map;

    // Fix for grey tiles - invalidate size after map is fully rendered
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
        console.log('🗺️ Map size invalidated (grey tiles fix)');
      }
    }, 100);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        initialCenterRef.current = null; // Reset for next mount
        mapInitializedRef.current = false;
      }
    };
  }, []); // Empty dependency array - only initialize once per component lifecycle

  // Update marker when coordinates change
  useEffect(() => {
    if (!mapRef.current || !selectedCoordinates) return;

    // Remove old marker
    if (markerRef.current) {
      mapRef.current.removeLayer(markerRef.current);
    }

    // Add new marker with custom icon - NEVER center, just place marker
    const marker = L.marker([selectedCoordinates.lat, selectedCoordinates.lng], {
      icon: customIcon
    }).addTo(mapRef.current);
    markerRef.current = marker;
  }, [selectedCoordinates]);

  // Show user's current location as blue circle
  useEffect(() => {
    if (!mapRef.current || !userLocation) return;

    // Remove old user marker
    if (userMarkerRef.current) {
      mapRef.current.removeLayer(userMarkerRef.current);
    }

    // Add user location marker (blue circle with pulsing animation)
    const userMarker = L.circleMarker([userLocation.lat, userLocation.lng], {
      radius: 8,
      fillColor: '#3B82F6',
      color: '#FFFFFF',
      weight: 2,
      opacity: 1,
      fillOpacity: 0.8
    }).addTo(mapRef.current);

    // Add accuracy circle
    L.circle([userLocation.lat, userLocation.lng], {
      radius: 50, // 50 meter accuracy radius
      fillColor: '#3B82F6',
      color: '#3B82F6',
      weight: 1,
      opacity: 0.2,
      fillOpacity: 0.1
    }).addTo(mapRef.current);

    userMarkerRef.current = userMarker;
  }, [userLocation]);

  return (
    <div className="relative w-full h-full">
      <div ref={mapContainerRef} className="absolute inset-0" />
    </div>
  );
};

export default MapView;
